<template>
  <div
    class="relative grid w-full max-w-[calc(100vw-2rem)] rounded-md bg-black p-2 shadow-md lg:p-4"
    :data-post-id="post.id"
  >
    <RouterLink
      class="block-link"
      :to="{ name: 'FanzonePost', params: { id: post.id } }"
    ></RouterLink>
    <PostMedia
      v-if="post.type"
      ref="postMediaRef"
      :id="post.id || ''"
      :type="post.type"
      :title="post.title"
      :imageUrl="post.image_url"
      :imageUrls="post.image_urls"
      :fullscreenAllowed="fullscreen_allowed"
      :is-active="isActive"
    />
    <div :class="post.type === 'image' || post.type === 'video' ? 'pt-2 lg:pt-4' : 'pt-0'">
      <div class="flex items-center justify-between">
        <span class="text-md flex font-Abel text-white opacity-60">{{ displayDateTime }}</span>
        <ShareButton
          :title="post.title"
          :text="post.text"
          :media="post.image_url"
          :url="postUrl || ''"
          @click.stop
        />
      </div>
      <div class="flex w-full items-center justify-between gap-x-4">
        <div class="text-sm">
          <h3 class="w-full font-Helvetica-Neue-Roman text-white/80">{{ post.title }}</h3>
          <div class="overflow-hidden">
            <div
              v-if="post.text"
              v-html="post.text"
              ref="postText"
              class="post-text mt-2"
              :class="{ 'post-text--expanded': isExpanded }"
            ></div>
            <div
              v-if="isTextTruncated"
              @click="toggleExpand"
              class="relative cursor-pointer bg-gradient-to-t from-black/100 from-50% to-black/50 pt-4 text-sm lowercase text-white/80 underline"
            >
              {{ isExpanded ? t('common.less') : t('common.more') }}
            </div>
          </div>
        </div>
        <!-- <div v-if="useFeatureComments" class="flex items-center">
          <Button
            variant="ghost"
            class="px-2 opacity-60 transition hover:opacity-100 relative z-index-1"
            @click="fetchComments"
          >
            <component
              class="!h-7 !w-7"
              :class="isCommentsFetching && 'animate-spin'"
              :is="isCommentsFetching ? Loader2 : MessageCircle"
            />
            <span class="font-semibold">{{ post.commentCount }}</span>
          </Button>
        </div> -->
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref, computed } from 'vue';
import type { PostInterface, PostProps } from './post.interface';
import moment from 'moment';
import { useI18n } from 'vue-i18n';
import { useCommentStore } from '@stores/comment.store';
import { featureFlags } from '@lib/feature.flags';

const { t } = useI18n();

// Components
import ShareButton from '@modules/Buttons/ShareButton.vue';
import PostMedia from '@/components/modules/Post/old/PostMedia.vue';
import Button from '@ui/button/Button.vue';

// Icons
import { MessageCircle, Loader2 } from 'lucide-vue-next';

const props = withDefaults(defineProps<PostProps>(), {
  post: (): PostInterface => ({
    id: '',
    artist_id: '',
    created_at: '',
    scheduled_at: '',
    image_url: '',
    image_urls: [],
    title: '',
    type: 'image',
    meta_data: {} as Record<string, unknown>,
    pinned: false,
    status: 'live',
    text: '',
    commentCount: 0,
  }),
  clickable: false,
  fullscreen_allowed: false,
});

const useFeatureComments = computed(() => featureFlags.comments);

const commentStore = useCommentStore();

const postText = ref<HTMLElement | null>(null);
const isExpanded = ref(false);
const isCommentsFetching = ref(false);

const displayDateTime = computed(() => {
  const scheduledAt = moment(props.post.scheduled_at);
  const createdAt = moment(props.post.created_at);
  if (scheduledAt.isAfter(createdAt)) {
    return scheduledAt.format('D MMMM');
  }
  return createdAt.format('D MMMM');
});

const postUrl = computed(() => {
  return `${import.meta.env.VITE_HOME_URL}/fanzone/${props.post.id}`;
});

const isTextTruncated = computed(() => {
  if (!postText.value) {
    return false;
  }
  return postText.value.scrollHeight > postText.value.clientHeight;
});

const fetchComments = async () => {
  if (!useFeatureComments.value) {
    return;
  }

  isCommentsFetching.value = true;
  await commentStore.openCommentDialog(true, props.post.id as string, props.post.title as string);
  isCommentsFetching.value = false;
};

const toggleExpand = () => {
  isExpanded.value = !isExpanded.value;
};

const postMediaRef = ref<InstanceType<typeof PostMedia> | null>(null);
const toggleFullscreen = () => {
  if (postMediaRef.value) {
    postMediaRef.value.toggleFullscreen();
  }
};

defineExpose({
  toggleFullscreen,
});
</script>

<style scoped>
.post-text:deep(p) {
  color: hsla(var(--primary), 0.8);
}
.post-text {
  max-height: 1.5rem;
  transition: all 0.5s ease-in-out;

  &--expanded {
    max-height: 1000px;
  }
}

.block-link {
  display: block;
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
}
</style>
